import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PostEditor } from '../components/post/PostEditor';
import { postsApi } from '../api/posts';
import type { CreatePostRequest } from '../types/post';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { addPost, setCurrentPost } from '../store/slices/postSlice';
import { useAuth } from '../auth/useAuth';
import { keycloakService } from '../auth/keycloakService';
import { useLanguage } from '../i18n/useLanguage';
import { Button } from '../components/common/Button';
import { PostInspirationHintBody } from '../components/post/PostInspirationHintBody';

export const CreatePostPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildCreateData = (data: CreatePostRequest | { title?: string; content?: string }): CreatePostRequest | null => {
    if (!data.content) {
      setError('Content is required');
      return null;
    }
    const createData: CreatePostRequest = {
      title: data.title,
      content: data.content,
      sightingType: 'sightingType' in data ? data.sightingType : undefined,
      tags: 'tags' in data ? (data as CreatePostRequest).tags : undefined,
    };
    if ('language' in data && data.language && data.language.toLowerCase() !== 'en') {
      createData.language = data.language;
    }
    return createData;
  };

  const submitPost = async (createData: CreatePostRequest) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const newPost = await postsApi.createPost(createData);
      dispatch(addPost(newPost));
      dispatch(setCurrentPost(newPost));
      navigate(`/posts/${newPost.id}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create post';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (data: CreatePostRequest | { title?: string; content?: string }) => {
    const createData = buildCreateData(data);
    if (!createData) return;
    await submitPost(createData);
  };

  const handleCancel = () => {
    navigate('/');
  };

  const goToLogin = () => {
    void keycloakService.login('/posts/new');
  };

  const goToRegister = () => {
    window.location.assign(keycloakService.getRegistrationUrl());
  };

  if (!isAuthenticated) {
    return (
      <div className="create-post-page">
        <div className="create-post-auth-gate">
          <h1>{t('post.createRequiresAccountTitle')}</h1>
          <p>{t('post.createRequiresAccountBody')}</p>
          <div className="create-post-auth-actions">
            <Button type="button" variant="primary" onClick={goToLogin}>
              {t('common.login')}
            </Button>
            <Button type="button" variant="outline" onClick={goToRegister}>
              {t('post.createRequiresAccountRegister')}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/')}>
              {t('post.createAuthBackHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-post-page">
      <h1>{t('post.createTitle')}</h1>
      {error && <div className="error-message">{error}</div>}
      <PostEditor
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
        requireEncounterDetails
      />
      <section className="post-inspiration-hint" aria-labelledby="post-inspiration-title">
        <h2 className="post-inspiration-hint-title" id="post-inspiration-title">
          ✍️ {t('post.inspirationTitle')}
        </h2>
        <div className="post-inspiration-hint-body">
          <PostInspirationHintBody />
        </div>
      </section>
    </div>
  );
};
